/**
 * Mock deal-health data for the admin splash page.
 *
 * Each entry is one JV submission with LLM-produced scores against multiple
 * strategy rubrics — wholesale and rentback today, extensible for flip /
 * rental / creative later. Shape mirrors what the real
 * `/admin/deals/health` endpoint should return once the scoring Lambda
 * ships, so swapping to the real API is a change of one function.
 *
 * The full rubric + prompt design lives in
 * tdb-backend/docs/deal-scoring.md. Weights and hard-failure logic in
 * scoreWholesale() and scoreRentback() below are ports of that doc.
 *
 * Backend origin (when real):
 *   - Fired from the same DynamoDB stream the comps worker uses
 *   - Scoring runs at TWO trigger points:
 *       (a) INSERT on tdb-data (sk = METADATA) → initial score for every strategy
 *       (b) MODIFY on the same key             → re-score when a scored input changes
 *   - Prompt is Claude Opus 5 with the comping evidence bundle + submission
 *     text + partner history + neighborhood enrichment
 *   - Output: { value, band, rationale[], flags[], hardFailures[], version, scoredAt }
 *   - Persisted alongside the submission as SCORE#<strategy>#<version> so the trend
 *     over re-scores is visible
 *
 * Ranking:
 *   - Default sort is by the top strategy score, ties broken by recency
 *   - The "Top 10 to call today" panel ranks by *potential* — top-score-weighted
 *     estimated wholesale fee — so a warm $12k deal is not buried under a
 *     hot $3k deal. See potentialRank on each deal.
 *
 * The distribution is deliberately unflattering: a small hot tail, a fat
 * warm middle, and a long cold tail. That is the real shape of an inbound
 * JV queue — a ranked page that pretends every deal is hot teaches nobody
 * to prioritise.
 */

export type Stage =
  | 'submitted'
  | 'reviewing'
  | 'contacted'
  | 'under_contract'
  | 'closed'
  // Terminal-negative statuses — the "dead lead" family. Backend has more
  // than one because the reason a deal died matters for partner metrics
  // and for de-duping future submissions.
  | 'dead'         // catch-all: unreachable, ghosted, unqualified
  | 'rejected'     // admin declined the deal
  | 'withdrawn'    // partner pulled it
  | 'duplicate';   // same property already in the pipeline

/** Every status that means "not in the active pipeline any more". */
export const DEAD_STAGES: readonly Stage[] = ['dead', 'rejected', 'withdrawn', 'duplicate'];

/** True when a deal still counts toward the live pipeline. */
export function isPipelineActive(stage: Stage): boolean {
  return stage !== 'closed' && !DEAD_STAGES.includes(stage);
}

/**
 * Pipeline value calculation cutoff. Deals submitted before this date do
 * not count toward the "total potential value" headline — the number is a
 * forward-looking view of the current book of work, not a historical total.
 */
export const PIPELINE_VALUE_CUTOFF = '2026-04-01T00:00:00Z';

export type Band = 'hot' | 'warm' | 'cold';

/**
 * Operational health, separate from the opportunity score.
 *
 *   fresh      — under 12h, no action needed yet
 *   cooling    — 12-24h in flight, admin should pick it up
 *   at_risk    — approaching the 24h SLA breach
 *   breached   — over 24h and still not contacted; live problem
 *   engaged    — someone has contacted or taken it; SLA satisfied
 *   dead       — closed, dead, or otherwise terminal; SLA does not apply
 */
export type Health = 'fresh' | 'cooling' | 'at_risk' | 'breached' | 'engaged' | 'dead';

/** Contact SLA: 72h for new submissions, 48h for existing (partner replied).
 *  See src/config/slaConstants.ts for the single source of truth. */
export { SLA_NEW_SUBMISSION_HOURS as CONTACT_SLA_HOURS } from '../config/slaConstants';

export interface DealFlag {
  /** Short label — rendered as a chip. */
  label: string;
  /** How the flag colours: positive = strengths the LLM picked out;
   *  concern = things that should slow the admin down; info = neutral. */
  tone: 'positive' | 'concern' | 'info';
}

/** Which deal-strategy rubrics scoring supports today. Extend by adding
 *  a key here and a scorer function; the payload shape is open. */
export type Strategy = 'wholesale' | 'rentback';

export interface StrategyScore {
  /** 3-97. Never 0 or 100 — no certainty about a deal you haven't touched. */
  value: number;
  band: Band;
  /** 2-4 bullets from the scorer, strongest signal first. */
  rationale: string[];
  flags: DealFlag[];
  /** Any hard-failure reasons that capped the score. Empty when none. */
  hardFailures: string[];
  /** The rubric+prompt version that produced this score. */
  version: string;
  scoredAt: string;
}

export interface DealScore {
  submissionId: string;
  submittedAt: string; // ISO
  submitter: {
    displayName: string;
    tenureWeeks: number;
    /** Historical close rate for this partner, 0-1. Missing when brand new. */
    priorCloseRate: number | null;
  };
  property: {
    address: string;
    city: string;
    state: string;
    /** Whether the comping engine could match this address. */
    compsAvailable: boolean;
    /** Only set when compsAvailable = true. */
    avmValue: number | null;
    partnerClaimedArv: number;
    /** Deterministic-engine confidence 0-1, only when compsAvailable. */
    compsConfidence: number | null;
    /** Signals from the JV submission that feed the scorers directly. */
    suggestedOffer: number;
    repairCost: number;
    mortgageBalance: number;
    monthlyIncome: number;
    /** Seller wants to stay in the property under a leaseback — the
     *  entry ticket for the rentback score. */
    sellerWantsToStay: boolean;
  };
  stage: Stage;
  /** Independent scores per strategy. Add a new key to add a new strategy. */
  scores: Record<Strategy, StrategyScore>;
  /** The strategy whose score leads by at least 15 points. `either` when
   *  the leader is within 15 of the runner-up, so admins can pursue
   *  whichever fits the seller. */
  recommendedStrategy: Strategy | 'either';
  /** The single number the queue ranks and displays by default —
   *  the max across all strategies. */
  topScore: number;
  /** Operational health — see Health type. Derived, not user-set. */
  health: Health;
  /** Estimated wholesale assignment fee for this deal in dollars. Used
   *  (with topScore) to rank the "Top 10 to call" panel — a warm large deal
   *  should outrank a hot tiny one. */
  estimatedWholesaleFee: number;
  /** Score-weighted potential = topScore/100 * estimatedWholesaleFee.
   *  Precomputed so the client doesn't have to. */
  potentialRank: number;
  /** Last time an admin touched this deal. Null = never contacted. */
  lastContactedAt: string | null;
  /** Which admin, if any, is currently owning follow-up. */
  ownedBy: string | null;
}

export interface DealHealthPayload {
  generatedAt: string;
  totals: {
    hot: number;
    warm: number;
    cold: number;
    uncontacted: number;
    /** Live count of deals past the 24h SLA that still have no contact.
     *  This is the number that should page someone. */
    slaBreached: number;
    /** Approaching the SLA — 18-24h uncontacted. */
    slaAtRisk: number;
  };
  /** Sum of estimated wholesale fee across active pipeline. Filtered to
   *  submissions on or after PIPELINE_VALUE_CUTOFF and only counting
   *  pipeline-active stages (excludes closed and all dead statuses). */
  pipelineValue: {
    total: number;
    dealCount: number;
    cutoffDate: string;
    /** Breakdown so the hero can show composition, not just the sum. */
    byStage: Partial<Record<Stage, number>>;
  };
  /** Convenience — the top 10 by score-weighted potential, already ranked.
   *  Prevents every client from re-sorting the full list. */
  topTen: DealScore[];
  deals: DealScore[];
}

// ---------------------------------------------------------------------------
// Seeded pseudo-randomness — same reasoning as leaderboardMock: a queue that
// reshuffles on every reload is impossible to evaluate.
// ---------------------------------------------------------------------------

function mulberry32(seed: number): () => number {
  let a = seed;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const FIRST = [
  'Sarah', 'Marcus', 'Denise', 'Tony', 'Priya', 'Jamal', 'Karen', 'Luis',
  'Rebecca', 'Andre', 'Michelle', 'Derek', 'Yolanda', 'Chris', 'Tamika',
  'Vince', 'Angela', 'Rob', 'Nadia', 'Kevin', 'Brenda', 'Omar', 'Lisa',
];
const LAST = 'ABCDEFGHIJKLMNOPRSTVW'.split('');

const CITIES: [string, string][] = [
  ['Trenton', 'NJ'], ['Camden', 'NJ'], ['Newark', 'NJ'], ['Paterson', 'NJ'],
  ['Baltimore', 'MD'], ['Wilmington', 'DE'], ['Philadelphia', 'PA'],
  ['Reading', 'PA'], ['Chester', 'PA'], ['Cleveland', 'OH'], ['Toledo', 'OH'],
  ['Detroit', 'MI'], ['Flint', 'MI'], ['Gary', 'IN'], ['Memphis', 'TN'],
  ['Birmingham', 'AL'], ['Jackson', 'MS'], ['Little Rock', 'AR'],
];

const STREETS = [
  'Elm', 'Maple', 'Oak', 'Cedar', 'Birch', 'Pine', 'Spruce', 'Chestnut',
  'Walnut', 'Sycamore', 'Poplar', 'Ash', 'Willow', 'Cypress', 'Locust',
];

/** The 60/30/10 shape of a real JV queue. */
export function bandFor(score: number): Band {
  if (score >= 75) return 'hot';
  if (score >= 45) return 'warm';
  return 'cold';
}

interface Draft {
  index: number;
  submittedAgoHours: number;
  partnerIdx: number;
  cityIdx: number;
  streetIdx: number;
  houseNumber: number;
  partnerClaimedArv: number;
  compsAvailable: boolean;
  avmValue: number | null;
  compsConfidence: number | null;
  /** How far apart partner ARV and AVM are, as a signed fraction. */
  arvDelta: number;
  /** Partner's suggested offer price to the seller. Feeds wholesale spread math. */
  suggestedOffer: number;
  /** Estimated repair cost as filled in by the partner. */
  repairCost: number;
  /** Seller's current mortgage balance, or 0 if free-and-clear. */
  mortgageBalance: number;
  /** Seller's declared monthly income — key rentback signal. */
  monthlyIncome: number;
  /** True when the seller wants to stay in the property under a leaseback. */
  sellerWantsToStay: boolean;
  /** True when there is an imminent tax sale date on the property. */
  taxSaleImminent: boolean;
  /** True when the owner's mailing state differs from the property state. */
  ownerOutOfState: boolean;
  tenureWeeks: number;
  priorCloseRate: number | null;
  stage: Stage;
  lastContactedAgoHours: number | null;
  ownedBy: string | null;
}

const ADMINS = ['Jordan', 'Marta', 'Ben'];

function makeDraft(i: number, rnd: () => number): Draft {
  const submittedAgoHours = Math.round(rnd() * 336); // 0-14 days
  const partnerIdx = Math.floor(rnd() * (FIRST.length * LAST.length));
  const cityIdx = Math.floor(rnd() * CITIES.length);
  const streetIdx = Math.floor(rnd() * STREETS.length);
  const houseNumber = 100 + Math.floor(rnd() * 8900);

  // 78% of submissions match a comped address.
  const compsAvailable = rnd() < 0.78;

  // Partner-claimed ARV clusters around $150k-$280k for these markets.
  const partnerClaimedArv = Math.round((140000 + rnd() * 180000) / 500) * 500;

  // When comps are available, the AVM diverges from the partner claim by
  // -35% to +25% — partners tend to over-claim, which is exactly the signal
  // the score should reward or punish.
  let avmValue: number | null = null;
  let compsConfidence: number | null = null;
  let arvDelta = 0;
  if (compsAvailable) {
    arvDelta = -0.35 + rnd() * 0.6;
    avmValue = Math.round((partnerClaimedArv * (1 + arvDelta)) / 500) * 500;
    compsConfidence = 0.35 + rnd() * 0.6;
  }

  // Tenure and prior close rate — a partner with a track record moves the
  // score more than any single-deal signal.
  const tenureWeeks = Math.round(2 + rnd() * 140);
  const priorCloseRate = tenureWeeks < 8 ? null : rnd() * 0.35;

  // Stage distribution — most inbound is still fresh. Dead-family stages
  // share the tail so the pipeline-value calc has realistic exclusions.
  const stageRoll = rnd();
  const stage: Stage =
    stageRoll < 0.50 ? 'submitted' :
    stageRoll < 0.70 ? 'reviewing' :
    stageRoll < 0.83 ? 'contacted' :
    stageRoll < 0.90 ? 'under_contract' :
    stageRoll < 0.94 ? 'closed' :
    stageRoll < 0.96 ? 'dead' :
    stageRoll < 0.98 ? 'rejected' :
    stageRoll < 0.99 ? 'withdrawn' :
    'duplicate';

  // Contact / ownership is only present past 'submitted'.
  const lastContactedAgoHours =
    stage === 'submitted' ? null : Math.round(rnd() * submittedAgoHours);
  const ownedBy =
    stage === 'submitted' ? null : ADMINS[Math.floor(rnd() * ADMINS.length)];

  // Wholesale/rentback signals — the numbers a real submission carries.
  //
  //   suggestedOffer: partner's proposed offer to the seller. Set below
  //     the AVM by a random discount so wholesale spread varies realistically.
  //   repairCost: mostly clusters $5k-$35k with a distressed tail up to $80k.
  //   mortgageBalance: ~65% of deals have a mortgage; balance is 20-80% of ARV.
  //   monthlyIncome: seller income $0-$12k/mo with most in $2-6k.
  //   sellerWantsToStay: ~30% — realistic for a mixed inbound.
  //   taxSaleImminent: ~15% (matches distressed lead sourcing).
  //   ownerOutOfState: ~22%.
  const suggestedOffer = Math.round(partnerClaimedArv * (0.45 + rnd() * 0.35) / 500) * 500;
  const repairCost = Math.round(Math.pow(rnd(), 1.4) * 75000 / 500) * 500;
  const hasMortgage = rnd() < 0.65;
  const mortgageBalance = hasMortgage
    ? Math.round(partnerClaimedArv * (0.2 + rnd() * 0.6) / 500) * 500
    : 0;
  const monthlyIncome = Math.round((500 + Math.pow(rnd(), 1.3) * 11500) / 100) * 100;
  const sellerWantsToStay = rnd() < 0.3;
  const taxSaleImminent = rnd() < 0.15;
  const ownerOutOfState = rnd() < 0.22;

  return {
    index: i,
    submittedAgoHours,
    partnerIdx,
    cityIdx,
    streetIdx,
    houseNumber,
    partnerClaimedArv,
    compsAvailable,
    avmValue,
    compsConfidence,
    arvDelta,
    suggestedOffer,
    repairCost,
    mortgageBalance,
    monthlyIncome,
    sellerWantsToStay,
    taxSaleImminent,
    ownerOutOfState,
    tenureWeeks,
    priorCloseRate,
    stage,
    lastContactedAgoHours,
    ownedBy,
  };
}

const SCORE_VERSION = 'v1-mock';

/** Clamp a raw sum into the score band. Never 0 or 100. */
function clampScore(n: number): number {
  return Math.max(3, Math.min(97, Math.round(n)));
}

interface ScoreOutput {
  value: number;
  rationale: string[];
  flags: DealFlag[];
  hardFailures: string[];
}

/**
 * Port of the wholesale rubric from tdb-backend/docs/deal-scoring.md §2.2.
 * The real production scorer is a Claude call with the rubric embedded;
 * this JS version exists so the mock UI reflects rubric-consistent scores
 * rather than random noise.
 */
function scoreWholesale(d: Draft): ScoreOutput {
  let s = 0;
  const rationale: string[] = [];
  const flags: DealFlag[] = [];
  const hardFailures: string[] = [];

  // #1 — Spread under 70% MAO (max 30)
  const anchorArv = d.compsAvailable && d.avmValue !== null
    ? Math.min(d.partnerClaimedArv, d.avmValue)
    : d.partnerClaimedArv;
  const mao = anchorArv * 0.70 - d.repairCost;
  const spread = mao - d.suggestedOffer;
  if (spread >= 25000) { s += 30; flags.push({ label: `$${Math.round(spread / 1000)}k spread`, tone: 'positive' }); rationale.push(`Spread of $${spread.toLocaleString()} under the 70% MAO — plenty of room for an assignment fee.`); }
  else if (spread >= 15000) { s += 22; flags.push({ label: `$${Math.round(spread / 1000)}k spread`, tone: 'positive' }); rationale.push(`Solid $${spread.toLocaleString()} spread — assignable at market fee.`); }
  else if (spread >= 10000) { s += 14; }
  else if (spread >= 5000) { s += 6; flags.push({ label: 'Thin spread', tone: 'concern' }); }
  else if (spread >= 0) { rationale.push(`Only $${spread.toLocaleString()} between the offer and the 70% MAO — no room for an assignment fee.`); flags.push({ label: 'No spread', tone: 'concern' }); }
  else { s -= 10; rationale.push(`Offer is $${(-spread).toLocaleString()} above the 70% MAO — the deal underwater before we assign.`); flags.push({ label: 'Underwater', tone: 'concern' }); }

  // #2 — Seller motivation (max 20). Wants-to-stay disqualifies wholesale.
  if (d.sellerWantsToStay) {
    hardFailures.push('Seller wants to stay — not a wholesale deal');
    flags.push({ label: 'Wants to stay', tone: 'concern' });
  }
  if (d.taxSaleImminent) {
    s += 20; flags.push({ label: 'Tax sale imminent', tone: 'positive' });
    rationale.push('Tax sale date on file — strong distress signal, seller likely to move fast.');
  } else if (d.ownerOutOfState || (d.mortgageBalance > 0 && d.mortgageBalance / anchorArv > 0.85)) {
    s += 12;
    if (d.ownerOutOfState) flags.push({ label: 'Out-of-state owner', tone: 'positive' });
    if (d.mortgageBalance > 0 && d.mortgageBalance / anchorArv > 0.85) flags.push({ label: 'Underwater mortgage', tone: 'positive' });
  } else {
    s += 3;
  }

  // #3 — Condition in sweet spot (max 15)
  if (d.repairCost >= 15000 && d.repairCost <= 50000) {
    s += 15; flags.push({ label: 'Investor-friendly condition', tone: 'positive' });
  } else if (d.repairCost < 5000) {
    s += 5; rationale.push('Nearly turn-key — competes with retail MLS buyers, not the wholesale sweet spot.');
  } else if (d.repairCost > 70000) {
    s += 5; flags.push({ label: 'Heavy rehab', tone: 'concern' });
  } else {
    s += 10;
  }

  // #4 — Cash-buyer demand (mocked as an implied 20% in this dataset)
  s += 6;

  // #5 — Realistic ARV (max 10)
  if (d.compsAvailable && d.avmValue !== null) {
    const over = -d.arvDelta;
    if (Math.abs(d.arvDelta) <= 0.10) { s += 10; }
    else if (Math.abs(d.arvDelta) <= 0.15) { s += 6; }
    else if (over >= 0.40) { s -= 15; hardFailures.push('ARV over-claim > 40% — spread is fictional'); flags.push({ label: `ARV over ${Math.round(over * 100)}%`, tone: 'concern' }); }
    else if (over >= 0.15) { flags.push({ label: `ARV over ${Math.round(over * 100)}%`, tone: 'concern' }); }
  } else {
    flags.push({ label: 'No comps', tone: 'concern' });
    if (d.compsAvailable === false) hardFailures.push('No comping match — value unverified');
  }

  // #6, #7, #8 — Assignability / title / property class assumed neutral in mock (max 5 each)
  s += 3 + 3 + 3;

  // Partner history is not a rubric line for wholesale, but a plausible bonus
  if (d.priorCloseRate !== null && d.priorCloseRate > 0.1) {
    s += 5; flags.push({ label: 'Proven partner', tone: 'positive' });
  } else if (d.priorCloseRate === null) {
    s -= 5; flags.push({ label: 'New partner', tone: 'info' });
  }

  // Hard-failure cap
  if (hardFailures.length > 0) {
    s = Math.min(s, 40);
    rationale.unshift(hardFailures[0]);
  }

  // Under-contract stage forces the score to reflect near-certain closing
  if (d.stage === 'under_contract') s = Math.max(s, 82);

  return { value: clampScore(s), rationale, flags, hardFailures };
}

/**
 * Port of the rentback rubric from tdb-backend/docs/deal-scoring.md §3.2.
 */
function scoreRentback(d: Draft): ScoreOutput {
  let s = 0;
  const rationale: string[] = [];
  const flags: DealFlag[] = [];
  const hardFailures: string[] = [];

  // #1 — Seller wants to stay (max 25). Hard failure otherwise.
  if (d.sellerWantsToStay) {
    s += 25; flags.push({ label: 'Seller wants to stay', tone: 'positive' });
    rationale.push('Seller wants to stay under a leaseback — the entry ticket for rentback.');
  } else {
    hardFailures.push('Seller does not want to stay — not a rentback');
    flags.push({ label: 'Wants out', tone: 'concern' });
  }

  // #2 — Rent-to-income sustainable (max 20)
  const anchorArv = d.compsAvailable && d.avmValue !== null ? d.avmValue : d.partnerClaimedArv;
  const estMarketRent = anchorArv * 0.008; // 0.8%/mo rough
  const rentToIncome = d.monthlyIncome > 0 ? estMarketRent / d.monthlyIncome : 1;
  if (rentToIncome <= 0.25) { s += 20; flags.push({ label: 'Rent sustainable', tone: 'positive' }); }
  else if (rentToIncome <= 0.35) { s += 12; }
  else if (rentToIncome <= 0.45) { s += 4; flags.push({ label: `Rent ${Math.round(rentToIncome * 100)}% of income`, tone: 'concern' }); }
  else {
    hardFailures.push('Seller income cannot cover plausible rent');
    flags.push({ label: `Rent ${Math.round(rentToIncome * 100)}% of income`, tone: 'concern' });
  }

  // #3 — Sufficient equity (max 15)
  const equityFrac = (anchorArv - d.mortgageBalance) / anchorArv;
  if (equityFrac >= 0.40) { s += 15; flags.push({ label: `${Math.round(equityFrac * 100)}% equity`, tone: 'positive' }); }
  else if (equityFrac >= 0.25) { s += 10; }
  else if (equityFrac >= 0.15) { s += 5; }
  else if (equityFrac >= 0.10) { /* 0 */ }
  else { hardFailures.push('Thin equity — cash-out math fails'); flags.push({ label: 'Thin equity', tone: 'concern' }); }

  // #4 — Property livable (max 15)
  if (d.repairCost < 5000) { s += 15; flags.push({ label: 'Livable now', tone: 'positive' }); }
  else if (d.repairCost <= 15000) { s += 8; }
  else {
    flags.push({ label: 'Not livable through rehab', tone: 'concern' });
    if (d.repairCost > 30000) hardFailures.push('Property needs heavy repairs — seller cannot live through the work');
  }

  // #5 — Cap-rate math (max 10). Rough: annual rent / (offer + $8k closing)
  const capRate = d.suggestedOffer > 0
    ? (estMarketRent * 12) / (d.suggestedOffer + 8000)
    : 0;
  if (capRate >= 0.09) { s += 10; flags.push({ label: `${Math.round(capRate * 100)}% cap`, tone: 'positive' }); }
  else if (capRate >= 0.08) { s += 6; }
  else if (capRate >= 0.07) { s += 2; }

  // #6, #7, #8 — market / understanding / employment — mocked neutral (max 5+5+5)
  s += 3 + 3 + 3;

  if (hardFailures.length > 0) {
    s = Math.min(s, 20);
    rationale.unshift(hardFailures[0]);
  }

  if (rationale.length < 2 && s >= 60) {
    rationale.push(
      `Estimated rent $${Math.round(estMarketRent).toLocaleString()}/mo against income $${d.monthlyIncome.toLocaleString()}/mo — sustainable.`,
    );
  }

  return { value: clampScore(s), rationale, flags, hardFailures };
}

function bandFromValue(v: number): Band {
  return bandFor(v);
}

/**
 * Operational health for the row. Distinct from `score` — that's the LLM's
 * opinion of the *deal*; this is the SLA state of the *ticket*.
 */
function healthFor(d: Draft): Health {
  // Any terminal stage (closed or dead-family) escapes the SLA framework.
  if (d.stage === 'closed' || DEAD_STAGES.includes(d.stage)) return 'dead';
  if (d.stage === 'under_contract' || d.stage === 'contacted') return 'engaged';
  // reviewing / submitted → SLA still applies until someone contacts.
  if (d.lastContactedAgoHours !== null) return 'engaged';
  if (d.submittedAgoHours > CONTACT_SLA_HOURS) return 'breached';
  if (d.submittedAgoHours > CONTACT_SLA_HOURS - 6) return 'at_risk';
  if (d.submittedAgoHours > 12) return 'cooling';
  return 'fresh';
}

function buildStrategyScore(out: ScoreOutput, scoredAt: string): StrategyScore {
  return {
    value: out.value,
    band: bandFromValue(out.value),
    rationale: out.rationale,
    flags: out.flags,
    hardFailures: out.hardFailures,
    version: SCORE_VERSION,
    scoredAt,
  };
}

function makeDeal(d: Draft): DealScore {
  const partnerFirst = FIRST[d.partnerIdx % FIRST.length];
  const partnerLast = LAST[Math.floor(d.partnerIdx / FIRST.length) % LAST.length];
  const [city, state] = CITIES[d.cityIdx];
  const street = STREETS[d.streetIdx];

  const submittedAt = new Date(Date.now() - d.submittedAgoHours * 3_600_000).toISOString();
  const lastContactedAt =
    d.lastContactedAgoHours === null
      ? null
      : new Date(Date.now() - d.lastContactedAgoHours * 3_600_000).toISOString();

  const scoredAt = submittedAt;
  const wholesale = buildStrategyScore(scoreWholesale(d), scoredAt);
  const rentback = buildStrategyScore(scoreRentback(d), scoredAt);

  const scores: Record<Strategy, StrategyScore> = { wholesale, rentback };
  const topScore = Math.max(wholesale.value, rentback.value);
  const gap = Math.abs(wholesale.value - rentback.value);
  const recommendedStrategy: Strategy | 'either' =
    gap < 15
      ? 'either'
      : wholesale.value >= rentback.value
        ? 'wholesale'
        : 'rentback';

  const health = healthFor(d);

  // Wholesale assignment fee — $5k-$15k range with a right skew. Stable
  // per submission via seeded index.
  const feeSeed = mulberry32(0xfee + d.index * 31);
  const feeBase = Math.pow(feeSeed(), 1.6);
  const estimatedWholesaleFee = Math.round((4500 + feeBase * 11000) / 250) * 250;

  return {
    submissionId: `jv_${String(d.index).padStart(4, '0')}`,
    submittedAt,
    submitter: {
      displayName: `${partnerFirst} ${partnerLast}.`,
      tenureWeeks: d.tenureWeeks,
      priorCloseRate: d.priorCloseRate,
    },
    property: {
      address: `${d.houseNumber} ${street} St`,
      city,
      state,
      compsAvailable: d.compsAvailable,
      avmValue: d.avmValue,
      partnerClaimedArv: d.partnerClaimedArv,
      compsConfidence: d.compsConfidence,
      suggestedOffer: d.suggestedOffer,
      repairCost: d.repairCost,
      mortgageBalance: d.mortgageBalance,
      monthlyIncome: d.monthlyIncome,
      sellerWantsToStay: d.sellerWantsToStay,
    },
    stage: d.stage,
    scores,
    recommendedStrategy,
    topScore,
    health,
    estimatedWholesaleFee,
    potentialRank: (topScore / 100) * estimatedWholesaleFee,
    lastContactedAt,
    ownedBy: d.ownedBy,
  };
}

const DEAL_COUNT = 48;

export async function fetchDealHealth(
  apiUrl: string,
  idToken: string,
): Promise<DealHealthPayload> {
  const resp = await fetch(`${apiUrl}/admin/deals/health`, {
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  });
  if (!resp.ok) {
    throw new Error(`Deal health fetch failed: ${resp.status} ${resp.statusText}`);
  }
  return await resp.json();
}

export function getMockDealHealth(): DealHealthPayload {
  const rnd = mulberry32(0xd3a1);
  const drafts = Array.from({ length: DEAL_COUNT }, (_, i) => makeDraft(i, rnd));
  const deals = drafts.map(makeDeal);

  // Ranked by top score across strategies, ties broken by recency.
  deals.sort((a, b) => {
    if (b.topScore !== a.topScore) return b.topScore - a.topScore;
    return new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime();
  });

  let hot = 0, warm = 0, cold = 0, uncontacted = 0;
  let slaBreached = 0, slaAtRisk = 0;
  for (const d of deals) {
    const band = bandFor(d.topScore);
    if (band === 'hot') hot++;
    else if (band === 'warm') warm++;
    else cold++;
    if (d.lastContactedAt === null && !DEAD_STAGES.includes(d.stage) && d.stage !== 'closed') {
      uncontacted++;
    }
    if (d.health === 'breached') slaBreached++;
    if (d.health === 'at_risk') slaAtRisk++;
  }

  // Top 10 by score-weighted potential. Excludes terminal stages — a closed
  // or dead deal isn't something to "call today".
  const topTen = [...deals]
    .filter((d) => isPipelineActive(d.stage))
    .sort((a, b) => b.potentialRank - a.potentialRank)
    .slice(0, 10);

  // Pipeline value: sum of estimatedWholesaleFee for deals that (a) are
  // submitted on or after the cutoff and (b) are still in the pipeline.
  const cutoffMs = new Date(PIPELINE_VALUE_CUTOFF).getTime();
  const eligible = deals.filter((d) =>
    isPipelineActive(d.stage) && new Date(d.submittedAt).getTime() >= cutoffMs,
  );
  const pipelineByStage: Partial<Record<Stage, number>> = {};
  let pipelineTotal = 0;
  for (const d of eligible) {
    pipelineTotal += d.estimatedWholesaleFee;
    pipelineByStage[d.stage] = (pipelineByStage[d.stage] ?? 0) + d.estimatedWholesaleFee;
  }

  return {
    generatedAt: new Date().toISOString(),
    totals: { hot, warm, cold, uncontacted, slaBreached, slaAtRisk },
    pipelineValue: {
      total: pipelineTotal,
      dealCount: eligible.length,
      cutoffDate: PIPELINE_VALUE_CUTOFF,
      byStage: pipelineByStage,
    },
    topTen,
    deals,
  };
}

export function stageLabel(stage: Stage): string {
  switch (stage) {
    case 'submitted': return 'Submitted';
    case 'reviewing': return 'Reviewing';
    case 'contacted': return 'Contacted';
    case 'under_contract': return 'Under contract';
    case 'closed': return 'Closed';
    case 'dead': return 'Dead';
  }
}

export function healthLabel(h: Health): string {
  switch (h) {
    case 'fresh': return 'Fresh';
    case 'cooling': return 'Cooling';
    case 'at_risk': return 'SLA at risk';
    case 'breached': return 'SLA breached';
    case 'engaged': return 'Engaged';
    case 'dead': return '—';
  }
}

/** Compact "3h ago" / "2d ago" formatter — avoids pulling in date-fns. */
export function relTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.max(0, Math.round(diffMs / 60_000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}
